import { useCallback, useEffect, useState } from "react";

import {
  boundsOf,
  loadGoogleMaps,
  MAP_OPTIONS,
  projectionFromMap,
  onMapsAuthFailure,
  tilesAreConfigured,
  type Coordinate,
  type GoogleMap,
  type Projection,
} from "../google-maps.ts";

/**
 * Mount a Google map in an element and keep a projection for whatever is drawn over it.
 *
 * The same shape as the effect in `apps/dispatch-console/src/ui/LiveMap.tsx`, and deliberately a
 * copy of it rather than a third thing: the part of that code which had bugs in it — the loader —
 * is shared through `@tdelivr/google-maps`, and this is the thirty React-coupled lines around it.
 * If the driver app wants the same thing, this is what should move into a package next; two call
 * sites is the point at which that is worth doing and one is not.
 *
 * ## What it gives back, and why that shape
 *
 * A `Projection` or nothing. The caller draws its markers as percentages of the surface either
 * way — from this projection when there is a map, from the design's own fixed percentages when
 * there is not — so a key that is missing, blocked or refused changes where the markers come
 * from and nothing else about the screen.
 */
export function useGoogleMap(points: readonly Coordinate[]): {
  readonly host: (node: HTMLDivElement | null) => void;
  readonly projection: Projection | undefined;
  /** True once Google has refused the key, so the surface can be taken back down. */
  readonly refused: boolean;
} {
  /*
   * A callback ref rather than `useRef`, and this is a bug rather than a preference.
   *
   * **Measured on the deployed rider app: the host element existed and no map was ever in
   * it.** The surface is only rendered once the screen has a coordinate, and on this
   * application's first screen that coordinate arrives from the browser's geolocation —
   * which is to say, after the first render. A `useRef` read inside an effect with an empty
   * dependency list is read exactly once, on mount, when the element it wants does not exist
   * yet; the effect returned early and never ran again. A callback ref fires when the element
   * attaches, which is the moment the map can actually be mounted.
   *
   * The dispatch console does not have this bug for a reason that is luck rather than care:
   * its surface is gated on the key alone, so the element is there on the first render.
   */
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const host = useCallback((node: HTMLDivElement | null) => {
    setElement(node);
  }, []);
  const [map, setMap] = useState<GoogleMap | undefined>(undefined);
  const [projection, setProjection] = useState<Projection | undefined>(undefined);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    if (!tilesAreConfigured() || element === null) return undefined;

    let live = true;
    let listener: { remove(): void } | undefined;

    /*
     * A key refused for its referrer fails here, not at load: Google checks it when the map asks
     * for a viewport. Dropping the projection puts every marker back on the design's own
     * percentages over the design's own surface, which is a screen that works.
     */
    const unregister = onMapsAuthFailure(() => {
      if (!live) return;
      setRefused(true);
      setProjection(undefined);
    });

    void loadGoogleMaps()
      .then((google) => {
        if (!live) return;
        const instance = new google.maps.Map(element, { ...MAP_OPTIONS });
        /*
         * `idle` rather than `bounds_changed`: the latter fires on every frame of a pan, and the
         * markers would be re-projected sixty times a second against a view still moving. They
         * travel with the tiles anyway, because the whole surface is one element.
         */
        listener = google.maps.event.addListener(instance, "idle", () => {
          setProjection(projectionFromMap(instance));
        });
        setMap(instance);
      })
      .catch((error: unknown) => {
        /*
         * A key refused for its referrer, a blocked script, a phone with no signal. The screen
         * keeps the design's schematic surface, which is a screen that works, so this is reported
         * rather than raised.
         */
        console.warn("Map tiles unavailable; drawing the design's surface", error);
      });

    return (): void => {
      live = false;
      listener?.remove();
      unregister();
    };
  }, [element]);

  /* Frame whatever the screen is drawing. One point centres; two or more are fitted. */
  useEffect(() => {
    if (map === undefined || points.length === 0) return;
    const google = (globalThis as { google?: Parameters<typeof boundsOf>[0] }).google;
    if (google === undefined) return;
    const bounds = boundsOf(google, points);
    if (bounds === undefined) return;
    map.fitBounds(bounds, 56);
    /*
     * A single point fits to a zero-sized box, which Google answers with its maximum zoom — a
     * street sign filling the hero. The fit is still what sets the centre; only the zoom is
     * overridden, and 15 is a few blocks, which is what "where you are" means on this screen.
     */
    if (points.length === 1) map.setZoom(15);
  }, [map, points]);

  return { host, projection, refused };
}
